import React, { useState } from 'react';
import styles from './AddClientContactModal.module.css';

const AddClientContactModal = ({ clientId, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    password_confirmation: '',
    job_title: '',
    phone_number: '',
    mobile_number: '',
    fax_number: '',
    department: '',
    reports_to: '',
    description: '',
    access_to_ntq: false,
    main_contact: false,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.password_confirmation.trim()) {
      newErrors.password_confirmation = 'Password confirmation is required';
    } else if (formData.password !== formData.password_confirmation) {
      newErrors.password_confirmation = 'Passwords do not match';
    }

    if (!formData.phone_number.trim()) {
      newErrors.phone_number = 'Phone number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setLoading(true);
      try {
        const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${backendUrl}/api/clients/${clientId}/contacts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify(formData),
        });

        const data = await response.json();

        if (response.ok) {
          onSave(data);
        } else {
          setErrors(data.errors || { general: data.message });
        }
      } catch (error) {
        console.error('Error adding contact:', error);
        setErrors({ general: 'Failed to add contact. Please try again.' });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <button type="button" className={styles.closeBtn} onClick={handleClose}>
            &times;
          </button>
          <h4 className={styles.modalTitle}>Add Company Contact</h4>
        </div>

        <div className={styles.modalBody}>
          <form onSubmit={handleSubmit}>
            {errors.general && (
              <div className={styles.errorAlert}>
                {errors.general}
              </div>
            )}

            <fieldset className={styles.formHorizontal}>
              <input type="hidden" name="client_id" value={clientId} />

              {/* First Name */}
              <div className={styles.formGroup}>
                <label htmlFor="first_name" className={`${styles.controlLabel} ${styles.labelRequired}`}>
                  First name:
                </label>
                <div className={styles.inputGroup}>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    className={`${styles.formControl} ${errors.first_name ? styles.errorInput : ''}`}
                    value={formData.first_name}
                    onChange={handleInputChange}
                    placeholder="First name"
                  />
                  {errors.first_name && (
                    <span className={styles.errorText}>{errors.first_name}</span>
                  )}
                </div>
              </div>

              {/* Last Name */}
              <div className={styles.formGroup}>
                <label htmlFor="last_name" className={`${styles.controlLabel} ${styles.labelRequired}`}>
                  Last name:
                </label>
                <div className={styles.inputGroup}>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    className={`${styles.formControl} ${errors.last_name ? styles.errorInput : ''}`}
                    value={formData.last_name}
                    onChange={handleInputChange}
                    placeholder="Last name"
                  />
                  {errors.last_name && (
                    <span className={styles.errorText}>{errors.last_name}</span>
                  )}
                </div>
              </div>

              {/* Email */}
              <div className={styles.formGroup}>
                <label htmlFor="email" className={`${styles.controlLabel} ${styles.labelRequired}`}>
                  Email:
                </label>
                <div className={styles.inputGroup}>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className={`${styles.formControl} ${errors.email ? styles.errorInput : ''}`}
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email"
                  />
                  {errors.email && (
                    <span className={styles.errorText}>{errors.email}</span>
                  )}
                </div>
              </div>

              {/* Password */}
              <div className={styles.formGroup}>
                <label htmlFor="password" className={`${styles.controlLabel} ${styles.labelRequired}`}>
                  Password:
                </label>
                <div className={styles.inputGroup}>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    className={`${styles.formControl} ${errors.password ? styles.errorInput : ''}`}
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Password"
                  />
                  {errors.password && (
                    <span className={styles.errorText}>{errors.password}</span>
                  )}
                </div>
              </div>

              {/* Password Confirmation */}
              <div className={styles.formGroup}>
                <label htmlFor="password_confirmation" className={`${styles.controlLabel} ${styles.labelRequired}`}>
                  Password confirmation:
                </label>
                <div className={styles.inputGroup}>
                  <input
                    type="password"
                    id="password_confirmation"
                    name="password_confirmation"
                    className={`${styles.formControl} ${errors.password_confirmation ? styles.errorInput : ''}`}
                    value={formData.password_confirmation}
                    onChange={handleInputChange}
                    placeholder="Password confirmation"
                  />
                  {errors.password_confirmation && (
                    <span className={styles.errorText}>{errors.password_confirmation}</span>
                  )}
                </div>
              </div>

              {/* Job Title */}
              <div className={styles.formGroup}>
                <label htmlFor="job_title" className={styles.controlLabel}>
                  Job title:
                </label>
                <div className={styles.inputGroup}>
                  <input
                    type="text"
                    id="job_title"
                    name="job_title"
                    className={styles.formControl}
                    value={formData.job_title}
                    onChange={handleInputChange}
                    placeholder="Job title"
                  />
                </div>
              </div>

              {/* Phone Number */}
              <div className={styles.formGroup}>
                <label htmlFor="phone_number" className={`${styles.controlLabel} ${styles.labelRequired}`}>
                  Phone number:
                </label>
                <div className={styles.inputGroup}>
                  <input
                    type="text"
                    id="phone_number"
                    name="phone_number"
                    className={`${styles.formControl} ${errors.phone_number ? styles.errorInput : ''}`}
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    placeholder="Phone number"
                  />
                  {errors.phone_number && (
                    <span className={styles.errorText}>{errors.phone_number}</span>
                  )}
                </div>
              </div>

              {/* Mobile Number */}
              <div className={styles.formGroup}>
                <label htmlFor="mobile_number" className={styles.controlLabel}>
                  Mobile number:
                </label>
                <div className={styles.inputGroup}>
                  <input
                    type="text"
                    id="mobile_number"
                    name="mobile_number"
                    className={styles.formControl}
                    value={formData.mobile_number}
                    onChange={handleInputChange}
                    placeholder="Mobile number"
                  />
                </div>
              </div>

              {/* Fax Number */}
              <div className={styles.formGroup}>
                <label htmlFor="fax_number" className={styles.controlLabel}>
                  Fax number:
                </label>
                <div className={styles.inputGroup}>
                  <input
                    type="text"
                    id="fax_number"
                    name="fax_number"
                    className={styles.formControl}
                    value={formData.fax_number}
                    onChange={handleInputChange}
                    placeholder="Fax number"
                  />
                </div>
              </div>

              {/* Department */}
              <div className={styles.formGroup}>
                <label htmlFor="department" className={styles.controlLabel}>
                  Department:
                </label>
                <div className={styles.inputGroup}>
                  <input
                    type="text"
                    id="department"
                    name="department"
                    className={styles.formControl}
                    value={formData.department}
                    onChange={handleInputChange}
                    placeholder="Department"
                  />
                </div>
              </div>

              {/* Reports To */}
              <div className={styles.formGroup}>
                <label htmlFor="reports_to" className={styles.controlLabel}>
                  Reports to:
                </label>
                <div className={styles.inputGroup}>
                  <input
                    type="text"
                    id="reports_to"
                    name="reports_to"
                    className={styles.formControl}
                    value={formData.reports_to}
                    onChange={handleInputChange}
                    placeholder="Reports to"
                  />
                </div>
              </div>

              {/* Description */}
              <div className={styles.formGroup}>
                <label htmlFor="description" className={styles.controlLabel}>
                  Description:
                </label>
                <div className={styles.inputGroup}>
                  <textarea
                    id="description"
                    name="description"
                    className={styles.formControl}
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Description"
                    rows="3"
                  />
                </div>
              </div>

              {/* Access to NTQ */}
              <div className={styles.formGroup}>
                <label className={styles.controlLabel}>
                  Access to NTQ:
                </label>
                <div className={styles.inputGroup}>
                  <input
                    type="checkbox"
                    id="access_to_ntq"
                    name="access_to_ntq"
                    checked={formData.access_to_ntq}
                    onChange={handleInputChange}
                  />
                  <label htmlFor="access_to_ntq" style={{ marginLeft: '8px', fontWeight: 'normal' }}>
                    Allow access to Notice to Quit
                  </label>
                </div>
              </div>

              {/* Main Contact */}
              <div className={styles.formGroup}>
                <label className={styles.controlLabel}>
                  Main contact:
                </label>
                <div className={styles.inputGroup}>
                  <input
                    type="checkbox"
                    id="main_contact"
                    name="main_contact"
                    checked={formData.main_contact}
                    onChange={handleInputChange}
                  />
                  <label htmlFor="main_contact" style={{ marginLeft: '8px', fontWeight: 'normal' }}>
                    Set as main contact
                  </label>
                </div>
              </div>

              {/* Submit Buttons */}
              <div className={styles.formGroup}>
                <div className={styles.inputGroup}>
                  <button
                    type="submit"
                    className={styles.btnPrimary}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Add Contact'}
                  </button>
                  <button
                    type="button"
                    className={styles.btnSecondary}
                    onClick={handleClose}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </fieldset>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddClientContactModal;
